/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/************************************************************************************

COMPONENT: l-collapsible-tab

*************************************************************************************/
class LTACollapsibleTab extends HTMLElement {
	constructor() {
		super()

		this.init()
	}

	disconnectedCallback() {
		this.destroy()
	}

	init() {
		this.setDefaultData()
		this.attachEvents()

		if (this.getAttribute('open') === 'true') {
			this.selected = true
			this.classList.add(this.expandedClass)
			this.setExpandedAria()
		} else {
			this.content.style.height = this.collapsedHeight
			this.classList.add(this.collapsedClass)
			this.setCollapsedAria()
		}

		this.content.removeAttribute('hidden')
	}

	setDefaultData() {
		this.expandedClass = 'is-expanded'
		this.collapsedClass = 'is-collapsed'

		this.transitionendEvent = this.transitionendEventName()

		this.trigger = this.querySelector('[data-trigger]')
		this.content = this.querySelector('[data-content]')

		this.collapsedHeight = '0px'
		this.defaultElements = ['a', 'button', 'input:not(.focus-none)', '[data-trigger]']
		this.oneAtATime = true

		this.dataset.oneOpen === 'false' && (this.oneAtATime = false)
	}

	attachEvents() {
		this.trigger.addEventListener('click', (e) => this.toggle(e))
	}

	toggle(e) {
		e.preventDefault()

		this.selected ? this.close() : this.open()

		/* Close another tab and open only the current tab. */
		if (this.selected && this.oneAtATime) {
			const tabsContainer = this.closest('[data-collapsible-container]')
			const collapsibleTabs = tabsContainer?.querySelectorAll('l-collapsible-tab')

			collapsibleTabs?.length &&
				collapsibleTabs.forEach((tab) => {
					tab !== this && tab.selected && tabsContainer !== tab && tab.close()
				})
		}
	}

	open() {
		this.selected = true
		this.expand()
		this.setAttribute('open', true)
	}

	close() {
		this.selected = false
		this.collapse()
		this.removeAttribute('open')
	}

	expand() {
		const onTransitionEnd = ((event) => {
			if (event.target === this.content) {
				this.content.removeEventListener(this.transitionendEvent, onTransitionEnd)

				this.isOpen &&
					requestAnimationFrame(() => {
						this.content.style.transition = '0'
						this.content.style.height = 'auto'

						requestAnimationFrame(() => {
							this.content.style.height = null
							this.content.style.transition = null
							this.setExpandedAria()
							this.trySetTabIndex(this.content, 0)
						})
					})
			}
		}).bind(this)

		this.content.addEventListener(this.transitionendEvent, onTransitionEnd)
		this.isOpen = true
		this.classList.add(this.expandedClass)
		this.classList.remove(this.collapsedClass)
		this.content.style.height = this.content.scrollHeight + 'px'
	}

	collapse() {
		const onTransitionEnd = ((event) => {
			if (event.target === this.content) {
				this.content.removeEventListener(this.transitionendEvent, onTransitionEnd)

				if (!this.isOpen) {
					this.setCollapsedAria()
					this.classList.add(this.collapsedClass)
					this.trySetTabIndex(this.content, -1)
				}
			}
		}).bind(this)

		this.content.addEventListener(this.transitionendEvent, onTransitionEnd)
		this.isOpen = false
		this.classList.remove(this.expandedClass)

		requestAnimationFrame(() => {
			this.content.style.transition = '0'
			this.content.style.height = this.content.scrollHeight + 'px'

			requestAnimationFrame(() => {
				this.content.style.transition = null
				this.content.style.height = this.collapsedHeight
			})
		})
	}

	destroy() {
		this.trigger && this.trigger.removeEventListener('click', (e) => this.toggle(e))
		this.content && this.content.removeAttribute('aria-hidden')
		this.content && (this.content.style.height = 'auto')

		this.classList.remove(this.expandedClass, this.collapsedClass)
		this.removeAttribute('open')
	}

	transitionendEventName() {
		let eventName,
			testElement = document.createElement('div'),
			eventNames = {
				transition: 'transitionend',
				OTransition: 'otransitionend',
				MozTransition: 'transitionend',
				WebkitTransition: 'webkitTransitionEnd',
			}

		for (eventName in eventNames) {
			if (eventNames.hasOwnProperty(eventName) && testElement.style[eventName] !== false) {
				return eventNames[eventName]
			}
		}
	}

	trySetTabIndex(parentElement, tabIndex) {
		const elements = parentElement.querySelectorAll(this.defaultElements)

		elements &&
			elements.forEach((e) => {
				e.setAttribute('tabindex', tabIndex)
			})
	}

	setExpandedAria() {
		this.trigger.setAttribute('aria-expanded', 'true')
		this.content.setAttribute('aria-hidden', 'false')
	}

	setCollapsedAria() {
		this.trigger.setAttribute('aria-expanded', 'false')
		this.content.setAttribute('aria-hidden', 'true')
	}
}

customElements.define('l-collapsible-tab', LTACollapsibleTab)

/************************************************************************************

COMPONENT: l-swiper-carousel

*************************************************************************************/
class LTASwiperCarousel extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		window.Swiper && this.setupCarousel()
	}

	setupCarousel() {
		this.carouselConfig = JSON.parse(this.querySelector('[data-carousel-config]').textContent)

		if (!this.carouselConfig) return

		console.log(this.querySelectorAll('.swiper-slide'))

		this.classList.remove('is-placeholder')
		this.querySelectorAll('.swiper-slide').forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

		this.swiperCarousel = new window.Swiper(this, this.carouselConfig)
	}
}

customElements.define('l-swiper-carousel', LTASwiperCarousel)

/************************************************************************************

COMPONENT: l-customer-reviews

*************************************************************************************/
class LTACustomerReviews extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		this.showMoreButtonEl = this.querySelector('[data-show-more-btn]')

		if (this.showMoreButtonEl) {
			this.showMoreButtonEl.addEventListener('click', () => {
				this.reviewCardEls = this.reviewCardEls || this.querySelectorAll('[data-review-card-mobile]')
				const isShowMoreMode = this.showMoreButtonEl.classList.toggle('is-active')

				this.reviewCardEls.forEach((reviewCardEl, index) => {
					reviewCardEl.classList.toggle('l-hidden', !isShowMoreMode && index > 1)
				})
			})
		}
	}
}

customElements.define('l-customer-reviews', LTACustomerReviews)

/************************************************************************************

COMPONENT: l-product-card

*************************************************************************************/
class LTAProductCard extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		this.addToCartBtnEl = this.querySelector('[data-add-to-cart]')
		this.formEl = this.querySelector('form')

		if (!this.addToCartBtnEl || !this.formEl) return

		this.addToCartBtnEl.addEventListener('click', () => {
			this.addToCartBtnEl.setAttribute('disabled', 'disabled')
			this.addToCartBtnEl.classList.add('is-loading')

			const productForm = new FormData(this.formEl)
			productForm.append('sections', ['mini-cart'])
			productForm.delete('option1')
			productForm.delete('option2')
			productForm.delete('option3')

			fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
				body: productForm,
				method: 'POST',
				headers: { 'X-Requested-With': 'XMLHttpRequest' },
			})
				.then((response) => response.json())
				.then((responseJson) => {
					if (responseJson.status) {
						this.dispatchEvent(
							new CustomEvent('cart-notification:show', {
								bubbles: true,
								cancelable: true,
								detail: { status: response.ok ? 'success' : 'error', error: responseJson.description || '' },
							})
						)
					}

					if (
						window.themeVariables.settings.cartType === 'page' ||
						window.themeVariables.settings.pageType === 'cart'
					) {
						return (window.location.href = `${Shopify.routes.root}cart`)
					}

					this.dispatchEvent(
						new CustomEvent('variant:added', {
							bubbles: true,
							detail: { variant: responseJson.hasOwnProperty('items') ? responseJson.items[0] : responseJson },
						})
					)

					fetch(`${window.themeVariables.routes.cartUrl}.js`).then(async (response2) => {
						const cartContent = await response2.json()
						document.documentElement.dispatchEvent(
							new CustomEvent('cart:updated', { bubbles: !0, detail: { cart: cartContent } })
						)
						cartContent.sections = responseJson.sections

						document.documentElement.dispatchEvent(
							new CustomEvent('cart:refresh', {
								bubbles: !0,
								detail: {
									cart: cartContent,
									openMiniCart:
										window.themeVariables.settings.cartType === 'drawer' && this.closest('.drawer') === null,
								},
							})
						)
					})
				})
				.finally(() => {
					this.addToCartBtnEl.removeAttribute('disabled')
					this.addToCartBtnEl.classList.remove('is-loading')
				})
		})
	}
}

customElements.define('l-product-card', LTAProductCard)

/************************************************************************************

COMPONENT: l-ugc-videos

*************************************************************************************/

class LTAUGCVideo extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Events
		this.playVideo()
	}

	playVideo() {
		this.addEventListener('click', () => {
			this.video = this.video || this.querySelector('video')

			if (this.video) {
				this.video.paused ? this.video.play() : this.video.pause()
			} else {
				const videoTemplate = this.querySelector('template').content.firstElementChild.cloneNode(true)
				this.querySelector('[data-slide-media]').appendChild(videoTemplate.querySelector('video'))

				this.video = this.querySelector('video')
				this.videoEvents()

				this.video.play()
			}
		})
	}

	videoEvents() {
		this.video.addEventListener('play', () => {
			this.classList.add('is-play')
		})

		this.video.addEventListener('pause', () => {
			this.classList.remove('is-play')
		})
	}
}

customElements.define('l-ugc-video', LTAUGCVideo)

